import React, { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle, CheckCircle2, Clock, Loader2, Pause, Pencil, Play, PlusCircle, Truck,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdate, fdatetime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/pages/crm/LeadForm";
import { STAGE_TONE, hrs } from "./Processing";

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-border/60 py-2 last:border-0">
    <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
    <span className="max-w-[62%] text-right text-sm font-medium">{value || "\u2014"}</span>
  </div>
);

export const ProcessingDetail = ({ jobId, open, meta, onOpenChange, onEdit, onChanged }) => {
  const { can } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("");        // rework | deliver | log
  const [text, setText] = useState("");
  const [link, setLink] = useState("");
  const [hours, setHours] = useState(1);

  const load = useCallback(async (silent = false) => {
    if (!jobId) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/processing/${jobId}`);
      setJob(data);
    } catch (e) { toast.error(errMsg(e)); onOpenChange(false); } finally { setLoading(false); }
  }, [jobId]); // eslint-disable-line

  useEffect(() => { if (open) load(); }, [open, jobId]); // eslint-disable-line
  useEffect(() => {
    if (!open || !job?.running) return;
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
  }, [open, job?.running, load]);

  const editable = can("processing", "edit");

  const act = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      setMode("");
      setText("");
      await load(true);
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const timer = (action) => act(() => api.post(`/processing/${job.id}/timer`, { action }),
    action === "start" ? "Timer running" : action === "pause" ? "Timer paused" : "Sent to internal QC");
  const stage = (s) => act(() => api.post(`/processing/${job.id}/stage`, { stage: s }), `Moved to ${s}`);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto scroll-slim p-0 sm:max-w-2xl">
        <SheetTitle className="sr-only">{job?.number || "Processing job"}</SheetTitle>
        <SheetDescription className="sr-only">Processing job time, stages, rework and delivery.</SheetDescription>

        {loading || !job ? (
          <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="bg-gradient-panel px-6 pb-5 pr-14 pt-8">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="num text-xs text-muted-foreground">{job.number} &middot; {job.service_type}</p>
                  <h2 className="truncate font-display text-2xl font-bold">{job.project_name}</h2>
                  <p className="truncate text-sm text-muted-foreground">{job.customer_name}{job.company ? ` \u00b7 ${job.company}` : ""}</p>
                </div>
                <StatusBadge status={job.stage} tone={STAGE_TONE[job.stage]} />
              </div>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5" data-testid="pd-active">
                  <Clock className="h-3.5 w-3.5" />{hrs(job.active_hours)} worked of {hrs(job.expected_hours)} expected
                </span>
                <span data-testid="pd-efficiency">Efficiency {job.efficiency}%</span>
                <span>{job.size_units} size unit(s) &middot; {job.complexity}</span>
                {job.due_date && <span>Due {fdate(job.due_date)}</span>}
                {job.rework_count > 0 && <span className="text-destructive">{job.rework_count} rework round(s)</span>}
              </div>

              {job.needs_resume && (
                <p className="mt-3 rounded-lg border border-warning/40 bg-warning-soft/50 px-3 py-2 text-xs font-medium text-warning"
                  data-testid="pd-resume-note">
                  The timer stopped by itself at the end of the working day. Start it again when you resume.
                </p>
              )}

              {editable && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {!["Delivered", "Cancelled"].includes(job.stage) && (job.running ? (
                    <>
                      <Button size="sm" variant="outline" data-testid="pd-pause" onClick={() => timer("pause")}>
                        <Pause className="h-3.5 w-3.5" /> Pause
                      </Button>
                      <Button size="sm" variant="success" data-testid="pd-finish" onClick={() => timer("finish")}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Finish processing
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" data-testid="pd-start" onClick={() => timer("start")}>
                      <Play className="h-3.5 w-3.5" /> {job.active_hours > 0 ? "Resume" : "Start"} work
                    </Button>
                  ))}
                  <Button size="sm" variant="outline" data-testid="pd-log-time" onClick={() => setMode("log")}>
                    <PlusCircle className="h-3.5 w-3.5" /> Add hours
                  </Button>
                  {job.stage !== "Delivered" && (
                    <Button size="sm" variant="outline" className="text-destructive" data-testid="pd-rework"
                      onClick={() => setMode("rework")}>
                      <AlertTriangle className="h-3.5 w-3.5" /> Raise rework
                    </Button>
                  )}
                  {job.stage !== "Delivered" && (
                    <Button size="sm" variant="soft" data-testid="pd-deliver" onClick={() => setMode("deliver")}>
                      <Truck className="h-3.5 w-3.5" /> Deliver
                    </Button>
                  )}
                  <Select value="" onValueChange={stage}>
                    <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="pd-stage-select">
                      <SelectValue placeholder="Change stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {(meta?.stages || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" data-testid="pd-edit" onClick={() => onEdit?.(job)}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                  <SheetClose asChild>
                    <Button size="sm" variant="ghost" data-testid="pd-close">Close</Button>
                  </SheetClose>
                </div>
              )}
            </div>

            <Tabs defaultValue="overview">
              <div className="scroll-slim overflow-x-auto border-b border-border px-6">
                <TabsList className="h-11 w-max bg-transparent p-0">
                  <TabsTrigger value="overview" data-testid="pd-tab-overview">Overview</TabsTrigger>
                  <TabsTrigger value="time" data-testid="pd-tab-time">
                    Time{(job.sessions || []).length ? ` (${job.sessions.length})` : ""}
                  </TabsTrigger>
                  <TabsTrigger value="rework" data-testid="pd-tab-rework">
                    Rework{job.rework_count ? ` (${job.rework_count})` : ""}
                  </TabsTrigger>
                  <TabsTrigger value="timeline" data-testid="pd-tab-timeline">Timeline</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="overview" className="m-0 px-6 py-5">
                <Row label="Engineer" value={job.engineer_name} />
                <Row label="Input data" value={[job.images ? `${job.images} images` : "",
                  job.size_gb ? `${job.size_gb} GB` : "", job.area_sqkm ? `${job.area_sqkm} sq km` : "",
                  job.sensor].filter(Boolean).join(" \u00b7 ")} />
                <Row label="Complexity" value={job.complexity} />
                <Row label="Priority" value={job.priority} />
                <Row label="Expected hours" value={`${job.expected_hours} h${Number(job.expected_hours_input || job.expected_hours_raw || 0) ? "" : " (benchmark)"}`} />
                <Row label="Throughput" value={job.throughput ? `${job.throughput} size unit(s) per hour` : ""} />
                <Row label="Deliver by" value={job.due_date ? fdate(job.due_date) : ""} />
                <Row label="Delivered" value={job.delivered_at ? fdatetime(job.delivered_at) : ""} />
                <Row label="Late by" value={job.late_days ? `${job.late_days} day(s)` : "on time"} />
                <Row label="Output format" value={job.output_format} />
                <Row label="Delivery link" value={job.output_link
                  ? <a href={job.output_link} target="_blank" rel="noreferrer" className="text-primary underline"
                      data-testid="pd-output-link">open</a> : ""} />
                <Row label="Delivery notes" value={job.output_notes} />
                <Row label="Input notes" value={job.input_notes} />
                <Row label="Created by" value={`${job.created_by_name} \u00b7 ${fdatetime(job.created_at)}`} />
              </TabsContent>

              <TabsContent value="time" className="m-0 space-y-2 px-6 py-5" data-testid="pd-sessions">
                {(job.sessions || []).length === 0 && (
                  <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                    No working time recorded yet.
                  </p>
                )}
                {(job.sessions || []).slice().reverse().map((s, i) => (
                  <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2">
                    <div>
                      <p className="text-sm font-semibold">{hrs((s.seconds || 0) / 3600)}
                        {s.manual ? " (added manually)" : s.auto_stopped ? " (auto stopped at day end)" : ""}</p>
                      <p className="text-xs text-muted-foreground">{fdatetime(s.start)} &rarr; {fdatetime(s.end)}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">{s.by}</span>
                  </div>
                ))}
                {job.running && (
                  <p className="text-xs font-medium text-success">Timer is running now - this session is counting.</p>
                )}
              </TabsContent>

              <TabsContent value="rework" className="m-0 space-y-2 px-6 py-5">
                {(job.rework || []).length === 0 && (
                  <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                    No rework on this job.
                  </p>
                )}
                {(job.rework || []).slice().reverse().map((r, i) => (
                  <div key={i} className="rounded-lg border border-destructive/30 bg-destructive-soft/30 px-3 py-2">
                    <p className="text-sm font-semibold">{r.reason}</p>
                    <p className="text-xs text-muted-foreground">{r.raised_by} &middot; {r.by} &middot; {fdatetime(r.at)}</p>
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="timeline" className="m-0 px-6 py-5">
                <div className="space-y-3">
                  {(job.history || []).slice().reverse().map((h, i) => (
                    <div key={i} className="relative pl-5">
                      <span className="absolute left-0 top-1.5 h-2 w-2 rounded-full bg-primary" />
                      <p className="text-sm font-semibold">{h.action}</p>
                      {h.note && <p className="text-xs text-muted-foreground">{h.note}</p>}
                      <p className="text-[11px] text-muted-foreground">{h.by} &middot; {fdatetime(h.at)}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}

        <Dialog open={!!mode} onOpenChange={(v) => !v && setMode("")}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">
                {mode === "rework" ? "Raise rework" : mode === "deliver" ? "Deliver the output" : "Add working hours"}
              </DialogTitle>
              <DialogDescription>
                {mode === "rework" ? "The engineer is notified and the job goes back to Rework."
                  : mode === "deliver" ? "Records the delivery and closes the job."
                  : "For work done outside the app. It is kept separate on the timeline."}
              </DialogDescription>
            </DialogHeader>
            {mode === "log" ? (
              <div className="space-y-3">
                <Field label="Hours" required>
                  <Input type="number" min="0.25" step="0.25" value={hours} data-testid="pd-hours"
                    onChange={(e) => setHours(e.target.value)} />
                </Field>
                <Field label="What was done">
                  <Input value={text} data-testid="pd-hours-note" onChange={(e) => setText(e.target.value)} />
                </Field>
              </div>
            ) : mode === "deliver" ? (
              <div className="space-y-3">
                <Field label="Delivery link">
                  <Input value={link} data-testid="pd-deliver-link" onChange={(e) => setLink(e.target.value)}
                    placeholder="Drive / WeTransfer link" />
                </Field>
                <Field label="Delivery notes">
                  <Textarea rows={2} value={text} data-testid="pd-deliver-notes" onChange={(e) => setText(e.target.value)} />
                </Field>
              </div>
            ) : (
              <Field label="What has to be corrected?" required>
                <Textarea rows={3} value={text} data-testid="pd-rework-reason" onChange={(e) => setText(e.target.value)} />
              </Field>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setMode("")} data-testid="pd-modal-cancel">Cancel</Button>
              <Button disabled={busy} data-testid="pd-modal-save" onClick={() => {
                if (mode === "rework") {
                  act(() => api.post(`/processing/${job.id}/rework`, { reason: text }), "Rework raised");
                } else if (mode === "deliver") {
                  act(() => api.post(`/processing/${job.id}/stage`, {
                    stage: "Delivered", output_link: link, output_notes: text,
                  }), "Delivered");
                } else {
                  act(() => api.post(`/processing/${job.id}/log-time`, { hours: Number(hours), note: text }),
                    "Hours added");
                }
              }}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
};
